import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSocket } from '@/contexts/SocketConnection';
import Alert from '../../feedback/Alert';
import { createVirtualBackground, DEFAULT_BACKGROUND, type VirtualBackground } from './virtualBackground';

/**
 * Sala de vídeo (C-34) — WebRTC P2P com sinalização Socket.IO na sala `encounter:<id>` (source/care/room/socket).
 *  - Ambos entram e avisam `rtc:ready`; o MÉDICO sempre cria a oferta (evita "glare").
 *  - Queda de conexão: o lado que volta reenvia `rtc:ready` e a negociação recomeça (janela de 30 min — RN-22).
 *  - STUN padrão stun:stun.l.google.com:19302; TURN/STUN configuráveis via `iceServers` (vem da API).
 */
export type VideoRoomState = 'starting' | 'waiting' | 'connecting' | 'live' | 'reconnecting' | 'media-error';

export interface VideoRoomProps {
  encounterId: string;
  role: 'patient' | 'doctor';
  iceServers?: RTCIceServer[];
  /** Rótulo do vídeo remoto (ex.: "Dr. João · Plantão Azanas"). */
  remoteLabel?: ReactNode;
  /** Botão vermelho de encerrar (omitido no totem). */
  onHangup?: () => void;
  hangupLabel?: string;
  /** O outro participante encerrou a chamada (`rtc:hangup`). */
  onRemoteHangup?: (reason?: string) => void;
  onStateChange?: (s: VideoRoomState) => void;
  /** Conteúdo sobreposto no topo (tag "ao vivo", cronômetro). */
  overlay?: ReactNode;
  waitingText?: string;
  tall?: boolean;
  /** Troca o fundo da câmera local por uma imagem (true = fundo padrão "consultório Azanas"). Se falhar, segue com a câmera normal. */
  virtualBackground?: boolean | string;
}

/** Tempo máximo esperando o fundo virtual carregar antes de entrar com a câmera normal. */
const BACKGROUND_TIMEOUT_MS = 10_000;

const DEFAULT_ICE: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

export default function VideoRoom({
  encounterId, role, iceServers, remoteLabel, onHangup, hangupLabel = 'Encerrar', onRemoteHangup, onStateChange, overlay,
  waitingText, tall, virtualBackground,
}: VideoRoomProps) {
  const socket = useSocket();
  const localRef = useRef<HTMLVideoElement>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRef = useRef<MediaStream | null>(null); // câmera "crua" (streamRef aponta para o vídeo com fundo trocado, quando ativo)
  const backgroundRef = useRef<VirtualBackground | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const pendingIce = useRef<RTCIceCandidateInit[]>([]);
  const [state, setStateRaw] = useState<VideoRoomState>('starting');
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const ice = iceServers?.length ? iceServers : DEFAULT_ICE;

  const cbs = useRef({ onRemoteHangup, onStateChange });
  cbs.current = { onRemoteHangup, onStateChange };
  const setState = useCallback((s: VideoRoomState) => { setStateRaw(s); cbs.current.onStateChange?.(s); }, []);

  const send = useCallback((event: string, data?: unknown) => socket?.emit(event, { encounterId, data }), [socket, encounterId]);

  /** Cria (ou recria) a conexão P2P com as faixas locais. */
  const newPeer = useCallback(() => {
    pcRef.current?.close();
    pendingIce.current = [];
    const pc = new RTCPeerConnection({ iceServers: ice });
    const local = streamRef.current;
    local?.getTracks().forEach((t) => pc.addTrack(t, local));
    // sem câmera/microfone (permissão negada): ainda assim recebe o vídeo do outro lado
    if (!local?.getAudioTracks().length && role === 'doctor') pc.addTransceiver('audio', { direction: 'recvonly' });
    if (!local?.getVideoTracks().length && role === 'doctor') pc.addTransceiver('video', { direction: 'recvonly' });
    pc.onicecandidate = (e) => { if (e.candidate) send('rtc:ice', e.candidate.toJSON()); };
    pc.ontrack = (e) => {
      if (remoteRef.current && remoteRef.current.srcObject !== e.streams[0]) {
        remoteRef.current.srcObject = e.streams[0] ?? null;
        void remoteRef.current.play().catch(() => {});
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc !== pcRef.current) return;
      if (pc.connectionState === 'connected') setState('live');
      else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        setState('reconnecting');
        // tenta renegociar: o paciente se anuncia de novo; o médico refaz a oferta ao receber
        setTimeout(() => { if (pcRef.current === pc && pc.connectionState !== 'connected') send('rtc:ready', { retry: true }); }, 2500);
      }
    };
    pcRef.current = pc;
    return pc;
  }, [ice, send, setState, role]);

  const makeOffer = useCallback(async () => {
    const pc = newPeer();
    setState('connecting');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send('rtc:offer', offer);
  }, [newPeer, send, setState]);

  const flushIce = async (pc: RTCPeerConnection) => {
    for (const c of pendingIce.current.splice(0)) await pc.addIceCandidate(c).catch(() => {});
  };

  // mídia local
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: { echoCancellation: true, noiseSuppression: true } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        cameraRef.current = stream;
        let outgoing = stream;
        if (virtualBackground) {
          // só entra na sala depois do fundo pronto: o paciente não pode ver o ambiente real do médico
          const creating = createVirtualBackground(stream, virtualBackground === true ? DEFAULT_BACKGROUND : virtualBackground).catch(() => null);
          const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), BACKGROUND_TIMEOUT_MS));
          const bg = await Promise.race([creating, timeout]);
          if (!bg) void creating.then((late) => late?.stop()); // chegou atrasado ou falhou: descarta
          if (cancelled) { bg?.stop(); stream.getTracks().forEach((t) => t.stop()); return; }
          if (bg) { backgroundRef.current = bg; outgoing = bg.stream; }
        }
        streamRef.current = outgoing;
        if (localRef.current) { localRef.current.srcObject = outgoing; void localRef.current.play().catch(() => {}); }
        setState('waiting');
      } catch (e) {
        // tenta só o microfone antes de desistir da mídia local
        const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
        if (audioOnly && !cancelled) {
          streamRef.current = audioOnly;
          setCam(false);
          setMediaError('Câmera indisponível — você está participando só com áudio.');
          setState('waiting');
          return;
        }
        setMediaError((e as Error).name === 'NotAllowedError'
          ? 'Permita o acesso à câmera e ao microfone nas configurações do navegador e recarregue a página. Sem mídia não há consulta.'
          : 'Não foi possível acessar câmera/microfone neste dispositivo — você verá e ouvirá o outro participante, mas ele não verá você.');
        setState('media-error');
      }
    })();
    return () => {
      cancelled = true;
      backgroundRef.current?.stop();
      backgroundRef.current = null;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      cameraRef.current?.getTracks().forEach((t) => t.stop());
      cameraRef.current = null;
    };
  }, [setState, virtualBackground]);

  // sinalização
  useEffect(() => {
    if (!socket || state === 'starting') return;
    const join = () => socket.emit('encounter:join', { encounterId }, (r: { ok: boolean }) => { if (r?.ok) send('rtc:ready'); });

    const onReady = ({ from, data }: { from: string; data: { reply?: boolean } | null }) => {
      if (from === role) return;
      if (role === 'doctor') void makeOffer();
      else if (!data?.reply) send('rtc:ready', { reply: true }); // garante que o médico (que pode ter chegado antes) saiba que estou aqui
    };
    const onOffer = async ({ from, data }: { from: string; data: RTCSessionDescriptionInit }) => {
      if (from === role || role !== 'patient') return;
      const pc = newPeer();
      setState('connecting');
      await pc.setRemoteDescription(data);
      await flushIce(pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send('rtc:answer', answer);
    };
    const onAnswer = async ({ from, data }: { from: string; data: RTCSessionDescriptionInit }) => {
      const pc = pcRef.current;
      if (from === role || !pc || pc.signalingState !== 'have-local-offer') return;
      await pc.setRemoteDescription(data);
      await flushIce(pc);
    };
    const onIce = async ({ from, data }: { from: string; data: RTCIceCandidateInit }) => {
      if (from === role || !data) return;
      const pc = pcRef.current;
      if (pc?.remoteDescription) await pc.addIceCandidate(data).catch(() => {});
      else pendingIce.current.push(data);
    };
    const onLeft = () => {
      pcRef.current?.close();
      pcRef.current = null;
      if (remoteRef.current) remoteRef.current.srcObject = null;
      setState('waiting');
    };
    const onHang = ({ from, data, reason }: { from?: string; data?: { reason?: string } | null; reason?: string }) => {
      if (from === role) return;
      cbs.current.onRemoteHangup?.(data?.reason ?? reason);
    };

    socket.on('rtc:ready', onReady);
    socket.on('rtc:offer', onOffer);
    socket.on('rtc:answer', onAnswer);
    socket.on('rtc:ice', onIce);
    socket.on('rtc:peer-left', onLeft);
    socket.on('rtc:hangup', onHang);
    socket.on('connect', join);
    if (socket.connected) join();
    return () => {
      socket.off('rtc:ready', onReady);
      socket.off('rtc:offer', onOffer);
      socket.off('rtc:answer', onAnswer);
      socket.off('rtc:ice', onIce);
      socket.off('rtc:peer-left', onLeft);
      socket.off('rtc:hangup', onHang);
      socket.off('connect', join);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, encounterId, role, state === 'starting']);

  // saída da sala
  useEffect(() => () => {
    socket?.emit('encounter:leave', { encounterId });
    pcRef.current?.close();
    pcRef.current = null;
  }, [socket, encounterId]);

  const toggleMic = () => { const v = !mic; streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = v)); setMic(v); };
  const toggleCam = () => { const v = !cam; streamRef.current?.getVideoTracks().forEach((t) => (t.enabled = v)); setCam(v); };
  const hangup = () => { send('rtc:hangup', { reason: role }); onHangup?.(); };

  const waitingMsg = waitingText ?? (role === 'patient' ? 'Aguardando o médico entrar na sala…' : 'Aguardando o paciente entrar na sala…');
  return (
    <div className="stack" style={{ ['--gap' as string]: '10px' }}>
      <div className="video-stage" style={tall ? { aspectRatio: '3 / 4', maxHeight: '62vh' } : undefined}>
        <video ref={remoteRef} className="remote" playsInline autoPlay style={{ display: state === 'live' ? 'block' : 'none' }} />
        {state !== 'live' && (
          <div className="stack center" style={{ padding: 16 }}>
            {state === 'media-error' ? <span style={{ fontSize: 40 }}>🎥</span> : <span className="spinner lg" />}
            <b>
              {state === 'starting' && 'Conectando à sala…'}
              {state === 'waiting' && waitingMsg}
              {state === 'connecting' && 'Conectando…'}
              {state === 'reconnecting' && 'Conexão perdida — reconectando… a sala fica ativa por 30 minutos.'}
              {state === 'media-error' && 'Câmera/microfone indisponíveis'}
            </b>
          </div>
        )}
        <video ref={localRef} className="local" muted playsInline autoPlay style={{ opacity: cam ? 1 : 0.25 }} />
        <div className="overlay-info">
          {state === 'live' && <span className="pill bad" style={{ background: 'var(--red)', color: '#fff' }}>● ao vivo</span>}
          {overlay}
        </div>
        {remoteLabel && state === 'live' && <div style={{ position: 'absolute', left: 12, bottom: 12, background: 'rgba(0,0,0,.45)', padding: '4px 10px', borderRadius: 8, fontSize: 13 }}>{remoteLabel}</div>}
      </div>
      {mediaError && <Alert tone="bad">{mediaError}</Alert>}
      <div className="video-controls">
        <button type="button" className={`round-btn ${mic ? '' : 'off'}`} aria-pressed={!mic} aria-label={mic ? 'Desligar microfone' : 'Ligar microfone'} title="Microfone" onClick={toggleMic} disabled={state === 'media-error'}>{mic ? '🎙️' : '🔇'}</button>
        <button type="button" className={`round-btn ${cam ? '' : 'off'}`} aria-pressed={!cam} aria-label={cam ? 'Desligar câmera' : 'Ligar câmera'} title="Câmera" onClick={toggleCam} disabled={state === 'media-error'}>{cam ? '📹' : '🚫'}</button>
        {onHangup && <button type="button" className="round-btn hang" aria-label={hangupLabel} title={hangupLabel} onClick={hangup}>📞</button>}
      </div>
    </div>
  );
}
